import { useToast } from '@chakra-ui/react';
import { AxiosResponse } from 'axios';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useHistory } from 'react-router';
import {
  deleteDummies,
  getAutomaticAnalysis,
  getContractById,
  getContracts,
  getContractsForDocuments,
  resetContracts,
} from '~/services';
import {
  AutomaticAnalysis,
  ContractForDocuments,
  ContractView,
  Pagination,
} from '~/types';
import { queryClient } from '~/utils/react-query';
import { QueryFilterContext } from '~/hooks/useFilter/types';

export function useContracts(
  page: number,
  search = '',
  status = '',
  orderby = '',
  order = '',
  faturamento = '',
  seguro = '',
  certificado = '',
  query?: QueryFilterContext,
  group = '',
  headOffice = '',
  branch = '',
  options: UseQueryOptions<Pagination<ContractView>, unknown> = {},
  page_size: number | undefined = undefined,
): UseQueryResult<Pagination<ContractView>, unknown> {
  const toast = useToast();
  const arrGroups = query?.groups?.map(e => e.value);
  const arrHeadOffices = query?.headOffices?.map(e => e.value);
  const arrBrancches = query?.branches?.map(e => e.value);
  return useQuery<Pagination<ContractView>, unknown>(
    [
      'contracts',
      page,
      search,
      status,
      orderby,
      order,
      faturamento,
      seguro,
      certificado,
      { arrGroups, arrHeadOffices, arrBrancches },
      group,
      headOffice,
      branch,
      page_size,
    ],
    () =>
      getContracts(
        page,
        search,
        status,
        orderby,
        order,
        faturamento,
        seguro,
        certificado,
        {
          groups: arrGroups,
          headoffices: arrHeadOffices,
          branches: arrBrancches,
        },
        group,
        headOffice,
        branch,
        page_size,
      ),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar contratos, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useContractsForDocuments(
  search = '',
  group = '',
  headOffice = '',
  branch = '',
  options: UseQueryOptions<ContractForDocuments[], unknown> = {},
): UseQueryResult<ContractForDocuments[], unknown> {
  const toast = useToast();
  return useQuery<ContractForDocuments[], unknown>(
    ['contracts_for_docs', search, group, headOffice, branch],
    () => getContractsForDocuments(search, group, headOffice, branch),
    {
      staleTime: 1000 * 60, // 1 minute
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar contratos, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useContract(
  id: number,
  options: UseQueryOptions<ContractView, unknown> = {},
): UseQueryResult<ContractView> {
  const toast = useToast();
  const history = useHistory();

  return useQuery<ContractView, unknown>(
    ['contract', id],
    () => getContractById(id),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar detalhes do contrato',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        history.goBack();
      },
    },
  );
}

export function useDeleteContractDuplicates(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  unknown,
  unknown
> {
  const toast = useToast();

  return useMutation(async () => deleteDummies(), {
    onSuccess: () => {
      toast({
        title: 'Contratos testes removidos',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      queryClient.invalidateQueries('contracts');
      queryClient.invalidateQueries('contract');
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Erro ao excluir contratos, tente novamente',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });
}

export function useResetContracts(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  unknown,
  unknown
> {
  const toast = useToast();

  return useMutation(async () => resetContracts(), {
    onSuccess: () => {
      toast({
        title: 'Dados removidos com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      queryClient.invalidateQueries('contracts');
      queryClient.invalidateQueries('contract');
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Erro ao excluir dados, tente novamente',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });
}

export function useAutomaticAnalysis(
  id: string,
  options: UseQueryOptions<AutomaticAnalysis, unknown> = {},
): UseQueryResult<AutomaticAnalysis> {
  const toast = useToast();
  const history = useHistory();

  return useQuery<AutomaticAnalysis, unknown>(
    ['automatic-analysis', id],
    () => getAutomaticAnalysis(id),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar análise automática do contrato',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        history.goBack();
      },
    },
  );
}
